import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ADC, CHAN, TIA, Reference, type ImpedanceExec } from '@/lib/protobuf';
import { useDeviceStore } from '@/store/useDeviceStore';
import { useHarnessStore } from '@/store/useHarnessStore';
import { profileService } from '@/services/api/profileService';
import logger from '@/lib/logger';
import { useHarnessMeasurement } from '../hooks/useHarnessMeasurement';

const TIA_OPTIONS: { value: TIA; label: string }[] = [
  { value: TIA.Resistor_200Ohm, label: '200 Ω' },
  { value: TIA.Resistor_1kOhm, label: '1 kΩ' },
  { value: TIA.Resistor_5kOhm, label: '5 kΩ' },
  { value: TIA.Resistor_10kOhm, label: '10 kΩ' },
  { value: TIA.Resistor_20kOhm, label: '20 kΩ' },
  { value: TIA.Resistor_40kOhm, label: '40 kΩ' },
  { value: TIA.Resistor_80kOhm, label: '80 kΩ' },
  { value: TIA.Resistor_160kOhm, label: '160 kΩ' },
];

const ADC_OPTIONS: { value: ADC; label: string }[] = [
  { value: ADC.Gain_1, label: '×1' },
  { value: ADC.Gain_1P5, label: '×1.5' },
  { value: ADC.Gain_2, label: '×2' },
  { value: ADC.Gain_4, label: '×4' },
  { value: ADC.Gain_9, label: '×9' },
];

/**
 * Fallback starting Excitation Profile, used only when the lab's default Measurement
 * Profile cannot be loaded (no default configured, or the request fails). On mount the
 * form is seeded from `profileService.getDefaultProfile()` when available. Researchers
 * then tune these values per run; the full profile is persisted exactly as sent.
 *
 * Mirrors the current default Measurement Profile ("Upper Respiratory Multisensor",
 * profile 10000000-0000-0000-0000-000000000002) — keep in sync with that profile's
 * ImpedanceExecJson whenever it changes.
 */
const DEFAULT_EXEC: ImpedanceExec = {
  amplitudeMV: [50],
  rtiaOhm: TIA.Resistor_10kOhm,
  biasMV: [200],
  adcGain: ADC.Gain_2,
  channels: [CHAN.CH1, CHAN.CH2, CHAN.CH3, CHAN.CH4],
  frequencyHz: [
    10000, 6787.07, 4606.75, 3127.47, 2124.32, 1444.77, 985.35, 675.75,
    467.94, 328.85, 235.65, 172.7, 129.49, 99.19, 77.41, 61.37, 49.26,
    39.93, 32.6, 26.75, 22.01, 18.13, 14.92, 12.25, 10,
  ],
  frequencyAuto: undefined,
  // Placeholder defaults pending firmware/domain sign-off (Story 1.6) — not tuned values.
  // periodsMin/durationMinMs use their proto minimum of 1 (0 is out of range for these two).
  intervalMs: [],
  settlePeriods: 0,
  settleMs: 0,
  periodsMin: 1,
  durationMinMs: 1,
  frequencySnapPct: 0,
  reference: Reference.BEFORE,
  hold: undefined,
};

/**
 * Validates a fetched default profile's user-facing fields against the same bounds manual entry
 * enforces (handleAmplitudeBlur/handleBiasBlur/handleFreqBlur below) — a misconfigured profile
 * must never reach the device with an out-of-range amplitude/bias or an empty frequency list.
 * An invalid field falls back to DEFAULT_EXEC's own value rather than the whole profile being
 * discarded. Returns the invalid field names so the caller can log what was overridden.
 */
function sanitizeExec(profileExec: ImpedanceExec): { exec: ImpedanceExec; invalidFields: string[] } {
  const invalidFields: string[] = [];

  let amplitudeMV = profileExec.amplitudeMV;
  const amplitudeVal = amplitudeMV?.[0];
  if (!Number.isFinite(amplitudeVal) || amplitudeVal! < 0 || amplitudeVal! > 808.8) {
    invalidFields.push('amplitudeMV');
    amplitudeMV = DEFAULT_EXEC.amplitudeMV;
  }

  let biasMV = profileExec.biasMV;
  const biasVal = biasMV?.[0];
  if (!Number.isFinite(biasVal) || biasVal! < -1100 || biasVal! > 1100) {
    invalidFields.push('biasMV');
    biasMV = DEFAULT_EXEC.biasMV;
  }

  let frequencyHz = profileExec.frequencyHz;
  if (
    !Array.isArray(frequencyHz) ||
    frequencyHz.length === 0 ||
    frequencyHz.some((v) => !Number.isFinite(v) || v <= 0)
  ) {
    invalidFields.push('frequencyHz');
    frequencyHz = DEFAULT_EXEC.frequencyHz;
  }

  return { exec: { ...profileExec, amplitudeMV, biasMV, frequencyHz }, invalidFields };
}

/**
 * Excitation Profile configuration + "Run Measurement" trigger.
 * Disabled unless there is an active Batch AND a connected device (AC1). On success the
 * completed Measurement is appended to the Batch's list immediately (AC2).
 */
export function MeasurementRunner() {
  const connectionState = useDeviceStore((s) => s.connectionState);
  const activeBatch = useHarnessStore((s) => s.activeBatch);
  const { runMeasurement, isRunning, progress, error } = useHarnessMeasurement();

  const [exec, setExec] = useState<ImpedanceExec>(DEFAULT_EXEC);
  const [amplitudeText, setAmplitudeText] = useState(String(DEFAULT_EXEC.amplitudeMV[0]));
  const [biasText, setBiasText] = useState(String(DEFAULT_EXEC.biasMV[0]));
  const [freqText, setFreqText] = useState(DEFAULT_EXEC.frequencyHz.join(', '));
  const [freqError, setFreqError] = useState<string | null>(null);

  // True once the researcher has made any edit — guards the profile-seed effect below from
  // overwriting in-progress work if the fetch resolves mid-edit.
  const touchedRef = useRef(false);
  const markTouched = () => {
    touchedRef.current = true;
  };

  // Seed the form once on mount from the lab's default Measurement Profile. The initial
  // state above (DEFAULT_EXEC) stays as the fallback if no default is configured or the
  // fetch fails, so the harness still works offline. Runs once; later edits are preserved.
  useEffect(() => {
    let cancelled = false;
    void profileService
      .getDefaultProfile()
      .then((profile) => {
        if (cancelled) return;
        // The researcher already started editing — never stomp their in-progress values.
        if (touchedRef.current) return;
        const { exec: next, invalidFields } = sanitizeExec(profile.impedanceExecJson);
        if (invalidFields.length > 0) {
          logger.warn(
            '[MeasurementRunner] Default Measurement Profile has out-of-range or invalid fields; using built-in fallback for:',
            invalidFields,
          );
        }
        setExec(next);
        setAmplitudeText(String(next.amplitudeMV[0]));
        setBiasText(String(next.biasMV[0]));
        setFreqText(next.frequencyHz.join(', '));
      })
      .catch((err) => {
        // Keep DEFAULT_EXEC — no default profile configured, or the request failed.
        logger.error('[MeasurementRunner] Failed to load default Measurement Profile:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isConnected = connectionState === 'connected';
  const canRun = isConnected && !!activeBatch && !isRunning && !freqError;

  const patch = (fields: Partial<ImpedanceExec>) => {
    markTouched();
    setExec((prev) => ({ ...prev, ...fields }));
  };

  const handleAmplitudeBlur = () => {
    const val = parseFloat(amplitudeText);
    if (isNaN(val) || val < 0 || val > 808.8) return;
    const rounded = Math.round(val * 10) / 10;
    patch({ amplitudeMV: [rounded] });
    setAmplitudeText(String(rounded));
  };

  const handleBiasBlur = () => {
    const val = parseFloat(biasText);
    if (isNaN(val) || val < -1100 || val > 1100) return;
    const rounded = Math.round(val * 10) / 10;
    patch({ biasMV: [rounded] });
    setBiasText(String(rounded));
  };

  const handleFreqBlur = () => {
    const parts = freqText
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const values = parts.map(Number);
    if (parts.length === 0 || values.some((v) => isNaN(v) || v <= 0)) {
      setFreqError('Enter one or more positive frequencies (Hz).');
      return;
    }
    setFreqError(null);
    patch({ frequencyHz: values });
  };

  const handleRun = () => {
    if (!canRun) return;
    // Ignore rejection here — the hook surfaces the message via `error`.
    void runMeasurement(exec).catch(() => {});
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 3 }}>
      <Typography variant="h6">Run Measurement</Typography>

      <Box>
        <Typography variant="body2" gutterBottom>
          Amplitude (mV) — range 0 – 808.8
        </Typography>
        <TextField
          type="number"
          size="small"
          value={amplitudeText}
          onChange={(e) => {
            markTouched();
            setAmplitudeText(e.target.value);
          }}
          onBlur={handleAmplitudeBlur}
          inputProps={{ min: 0, max: 808.8, step: 0.1 }}
          sx={{ width: 160 }}
        />
      </Box>

      <Box>
        <Typography variant="body2" gutterBottom>
          Bias Voltage (mV) — range -1100 – 1100
        </Typography>
        <TextField
          type="number"
          size="small"
          value={biasText}
          onChange={(e) => {
            markTouched();
            setBiasText(e.target.value);
          }}
          onBlur={handleBiasBlur}
          inputProps={{ min: -1100, max: 1100, step: 0.1 }}
          sx={{ width: 160 }}
        />
      </Box>

      <FormControl size="small" sx={{ width: 200 }}>
        <InputLabel>TIA Resistor</InputLabel>
        <Select
          label="TIA Resistor"
          value={exec.rtiaOhm}
          onChange={(e) => patch({ rtiaOhm: e.target.value as TIA })}
        >
          {TIA_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ width: 200 }}>
        <InputLabel>ADC Gain</InputLabel>
        <Select
          label="ADC Gain"
          value={exec.adcGain}
          onChange={(e) => patch({ adcGain: e.target.value as ADC })}
        >
          {ADC_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box>
        <Typography variant="body2" gutterBottom>
          Frequencies (Hz) — {exec.frequencyHz.length} point
          {exec.frequencyHz.length !== 1 ? 's' : ''}
        </Typography>
        <TextField
          multiline
          rows={3}
          fullWidth
          size="small"
          value={freqText}
          onChange={(e) => {
            markTouched();
            setFreqText(e.target.value);
          }}
          onBlur={handleFreqBlur}
          placeholder="e.g. 10, 100, 1000, 10000"
          error={!!freqError}
          helperText={freqError ?? 'Comma or space-separated Hz values'}
        />
      </Box>

      <Button variant="contained" onClick={handleRun} disabled={!canRun}>
        {isRunning ? 'Running Measurement…' : 'Run Measurement'}
      </Button>

      {isRunning && <LinearProgress variant="determinate" value={progress} />}

      {!activeBatch && (
        <Typography variant="body2" color="text.secondary">
          Start a Batch to run a Measurement.
        </Typography>
      )}
      {!isConnected && (
        <Typography variant="body2" color="text.secondary">
          Connect a device to run a Measurement.
        </Typography>
      )}

      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  );
}
